/**
 * AgreementReportsSection — "Portfolio, macro and market reports" (MR Inc 4.7).
 *
 * The first of the two Documents-tab lists (the other is "Legal documents"). These are the
 * regular commercial/investment reports with one EDITION PER MONTH — so this list carries a
 * month picker + a View button, unlike the latest-only legal docs. v1 has one active report,
 * the Monthly Report (produced-and-recorded; retrieved here by any user with agreement
 * access); the other rows are placeholders for future report types. Collapsible, expanded on
 * start. PDFs open in a new tab (CSP-safe; C-260).
 *
 * Endpoints (Inc 4.5): GET /agreements/{id}/monthly-reports + recorded-monthly-report.pdf.
 */
import { useState } from 'react';
import { Button, Card, Checkbox, Collapse, Group, Select, Stack, Table, Text, Title, Tooltip, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconChevronDown, IconChevronRight, IconDownload, IconExternalLink, IconFileText, IconFileZip,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';

import { clientReportsApi } from '../api/clientReports';
import type { UUID } from '../api/types';
import { DisabledTooltipButton } from './DisabledTooltipButton';

const REPORT_ROWS: { key: string; label: string; active: boolean }[] = [
  { key: 'mr', label: 'Monthly report', active: true },
  { key: 'feeinvoice', label: 'Fee invoice', active: false },
  { key: 'bankval', label: 'Bank valuation', active: false },
  { key: 'insval', label: 'Insurance policy valuation', active: false },
  { key: 'mifidcosts', label: 'MiFID fees & costs report', active: false },
];

function ymOf(iso: string): { year: number; month: number } {
  return { year: Number(iso.slice(0, 4)), month: Number(iso.slice(5, 7)) };
}

export function AgreementReportsSection({ agreementId }: { agreementId: UUID }) {
  const [opened, { toggle }] = useDisclosure(true); // expanded on start
  const reportsQ = useQuery({
    queryKey: ['agreement-monthly-reports', agreementId],
    queryFn: () => clientReportsApi.listAgreementReports(agreementId),
  });
  const months = reportsQ.data ?? []; // newest month first
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null); // no auto-default
  const [sel, setSel] = useState<Set<string>>(new Set()); // start unselected

  // The Monthly Report has an edition for the picked month iff that month is on record.
  const mrAvailable = !!selectedMonth && months.some((m) => m.period_month === selectedMonth);
  const actionable = (key: string): boolean => key === 'mr' && mrAvailable;

  function toggleRow(key: string, checked: boolean) {
    setSel((prev) => { const n = new Set(prev); if (checked) n.add(key); else n.delete(key); return n; });
  }

  function view() {
    if (!selectedMonth) return;
    const { year, month } = ymOf(selectedMonth);
    if (sel.has('mr') && mrAvailable) {
      window.open(`/agreements/${agreementId}/recorded-monthly-report.pdf?year=${year}&month=${month}`, '_blank');
    }
  }
  async function downloadIndividual() {
    if (!selectedMonth) return;
    const { year, month } = ymOf(selectedMonth);
    try {
      if (sel.has('mr') && mrAvailable) await clientReportsApi.downloadRecorded(agreementId, year, month);
    } catch (err) {
      notifications.show({
        color: 'red', title: 'Download failed', position: 'top-center',
        message: err instanceof Error ? err.message : 'Could not download the report.',
      });
    }
  }

  const nSel = REPORT_ROWS.filter((r) => sel.has(r.key) && actionable(r.key)).length;
  const noMonth = !selectedMonth;
  // Emailing a report: the page header's Email (P34, Pedro 2026-10-14, option A: one way in).
  return (
    <Card withBorder p="lg" radius="md">
      <Stack gap="sm">
        <UnstyledButton onClick={toggle}>
          <Group gap="xs" align="center">
            {opened ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />}
            <IconFileText size={20} />
            <Title order={4}>Portfolio, macro and market reports</Title>
          </Group>
        </UnstyledButton>

        <Collapse in={opened}>
          <Stack>
            <Text size="sm" c="dimmed">
              Regular commercial reports with monthly editions. Pick a month, tick the report(s),
              then View or Download. Reports appear here once Admin produces them (Admin → Client Reports).
            </Text>

            {months.length === 0 ? (
              <Text size="sm" c="dimmed">No reports on record yet.</Text>
            ) : (
              <>
                <Group justify="space-between" align="flex-end">
                  <Select
                    label="Month" placeholder="Pick a month"
                    data={months.map((m) => ({ value: m.period_month, label: m.period_month.slice(0, 7) }))}
                    value={selectedMonth} onChange={setSelectedMonth} maw={160}
                  />
                  <Group gap="sm">
                    {noMonth ? (
                      <DisabledTooltipButton size="xs" variant="light" leftSection={<IconExternalLink size={14} />} disabledReason="Select a month first.">View</DisabledTooltipButton>
                    ) : (
                      <Button size="xs" variant="light" leftSection={<IconExternalLink size={14} />} onClick={view} disabled={nSel === 0}>View</Button>
                    )}
                    {noMonth ? (
                      <DisabledTooltipButton size="xs" leftSection={<IconDownload size={14} />} disabledReason="Select a month first.">Download individual</DisabledTooltipButton>
                    ) : (
                      <Button size="xs" leftSection={<IconDownload size={14} />} onClick={downloadIndividual} disabled={nSel === 0}>Download individual ({nSel})</Button>
                    )}
                    <DisabledTooltipButton size="xs" variant="light" leftSection={<IconFileZip size={14} />} disabledReason="Bundles multiple reports — only the Monthly report exists today.">Download zip</DisabledTooltipButton>
                  </Group>
                </Group>

                <Table withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Document</Table.Th>
                      <Table.Th style={{ width: 120 }} />
                      <Table.Th style={{ width: 120 }} />
                      <Table.Th style={{ width: 120 }} />
                      <Table.Th style={{ width: 120 }} />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {REPORT_ROWS.map((r) => {
                      const on = actionable(r.key);
                      const reason = !r.active
                        ? 'Available in a later version.'
                        : (noMonth ? 'Select a month first.' : 'No edition for the selected month.');
                      const box = (
                        <Checkbox
                          size="sm" aria-label={r.label} checked={sel.has(r.key)} disabled={!on}
                          onChange={(e) => toggleRow(r.key, e.currentTarget.checked)}
                        />
                      );
                      return (
                        <Table.Tr key={r.key}>
                          <Table.Td>
                            <Text size="sm" fw={500} c={r.active ? undefined : 'dimmed'}>{r.label}</Text>
                            {!r.active && <Text size="xs" c="dimmed" mt={2}>Not available yet</Text>}
                          </Table.Td>
                          <Table.Td /><Table.Td /><Table.Td />
                          <Table.Td style={{ textAlign: 'center' }}>
                            <Group justify="center" gap={0}>
                              {on ? box : (
                                <Tooltip label={reason} withArrow>
                                  <span style={{ display: 'inline-flex' }}>{box}</span>
                                </Tooltip>
                              )}
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </>
            )}
          </Stack>
        </Collapse>
      </Stack>
    </Card>
  );
}
